import { Dispatch, Fragment, FunctionComponent, SetStateAction, useCallback, useEffect, useState } from 'react';
import { downloadAsCsvFile } from '../../../common/utils/csvHelper';
import { tryFormatDateTimeRange } from '../../../common/utils/dateUtils';
import BookingModal from '../../bookingModal/BookingModal';
import Pagination from '../../shared/Pagination';
import { SortDirection, Table, TableHead, TableHeader } from '../../shared/Table';
import { usePagination } from '../../shared/usePagination';
import { BookingsSortOrder } from '../bookingConstants';
import { Booking } from '../models/Booking';
import { BookingStatus } from '../models/BookingStatus';
import { InvoiceStatus } from '../models/InvoiceStatus';
import { useBookingHistory } from '../useBookingHistory';
import AssetFilter from './AssetFilter';
import BookingItem from './BookingItem';
import NoBookings from './NoBookings';

type Props = {
    bookings: Booking[] | undefined;
    defaultSortDirection: SortDirection;
    defaultSortOrder: BookingsSortOrder;
    filterNoBookingsHeading: string;
    filterNoBookingsText: string;
    hasAssetFilter: boolean;
    isArchived: boolean;
    isLoading: boolean;
    noBookingsHeading: string;
    noBookingsText: string;
    onAssetFilterChange: Dispatch<SetStateAction<string | undefined>>;
    refetch: () => void;
};

const BookingList: FunctionComponent<Props> = ({
    bookings,
    defaultSortDirection,
    defaultSortOrder,
    filterNoBookingsHeading,
    filterNoBookingsText,
    hasAssetFilter,
    isArchived,
    isLoading,
    noBookingsHeading,
    noBookingsText,
    onAssetFilterChange,
    refetch,
}) => {
    const [bookingId, setBookingId] = useState<string | undefined>();
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    const [sortedBookings, setSortedBookings] = useState<Booking[]>([]);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    useBookingHistory(setBookingId);

    useEffect(() => {
        const bookingsSorted = sortBookings(bookings || [], sortOrder);

        if (sortDirection === 'descending') {
            bookingsSorted.reverse();
        }

        setSortedBookings(bookingsSorted);
    }, [bookings, sortOrder, sortDirection]);

    const { currentPage, setCurrentPage, currentItems: currentBookings, totalPages, itemsPerPage } = usePagination(sortedBookings);

    const sortItems = (sortBy: BookingsSortOrder) => {
        if (sortBy !== sortOrder) {
            setSortDirection('ascending');
            setSortOrder(sortBy);
            return;
        }

        if (sortDirection === 'ascending') {
            setSortDirection('descending');
            return;
        }

        if (sortDirection === 'descending') {
            setSortDirection('none');
            return;
        }

        setSortDirection('ascending');
    };

    const getSortDirection = (sortBy: BookingsSortOrder): SortDirection => {
        if (sortBy !== sortOrder) {
            return 'none';
        }
        return sortDirection;
    };

    const exportBookings = useCallback(() => {
        if (!sortedBookings) {
            return;
        }

        const data = sortedBookings.map((x) => {
            return [
                `#${x.bookingReference}`,
                x.assetName,
                tryFormatDateTimeRange(x.startTime, x.endTime),
                x.userName ?? '-',
                x.status === BookingStatus.Cancelled ? 'Avbokad' : 'Genomförd',
                getInvoiceStatusText(x.generalInvoiceStatus, x.status, x.hasFreeCancellation),
                x.apartmentAddress ?? '',
                x.userPhoneNumber ?? '',
            ];
        });

        data.unshift(['Bokningsref.', 'Resurs', 'Tid', 'Bokad av', 'Status', 'Fakturastatus', 'Adress', 'Telefon']);
        downloadAsCsvFile(data, 'Bokningshistorik.csv');
    }, [sortedBookings]);

    const handleAssetFilterChange = useCallback<Dispatch<SetStateAction<string | undefined>>>(
        (assetId) => {
            onAssetFilterChange(assetId);
            setCurrentPage(1); // Reset to first page when filter changes
        },
        [onAssetFilterChange, setCurrentPage],
    );

    if (!bookings) {
        return null;
    }

    return (
        <Fragment>
            {bookings.length === 0 && !hasAssetFilter && !isLoading ? (
                <NoBookings noBookingsHeading={noBookingsHeading} noBookingsText={noBookingsText} />
            ) : (
                <Fragment>
                    <AssetFilter exportBookings={exportBookings} setAssetId={handleAssetFilterChange} />
                    <Table withPagination={totalPages > 1}>
                        <TableHead>
                            <tr>
                                {isArchived && (
                                    <TableHeader
                                        onClick={() => sortItems(BookingsSortOrder.Reference)}
                                        sortDirection={getSortDirection(BookingsSortOrder.Reference)}
                                        className="d-none d-md-table-cell"
                                    >
                                        Bokningsref.
                                    </TableHeader>
                                )}
                                <TableHeader onClick={() => sortItems(BookingsSortOrder.Name)} sortDirection={getSortDirection(BookingsSortOrder.Name)}>
                                    Resurs
                                </TableHeader>
                                <TableHeader onClick={() => sortItems(BookingsSortOrder.Date)} sortDirection={getSortDirection(BookingsSortOrder.Date)}>
                                    Tid
                                </TableHeader>
                                <TableHeader onClick={() => sortItems(BookingsSortOrder.BookedBy)} sortDirection={getSortDirection(BookingsSortOrder.BookedBy)} className="d-none d-md-table-cell">
                                    Bokad av
                                </TableHeader>
                                <th className="d-none d-md-table-cell"></th>
                            </tr>
                        </TableHead>
                        <tbody>
                            {currentBookings.map((x) => (
                                <BookingItem booking={x} isArchived={isArchived} key={x.bookingId} showDetails={setBookingId} />
                            ))}
                        </tbody>
                    </Table>
                    {!bookings?.length && <NoBookings noBookingsHeading={filterNoBookingsHeading} noBookingsText={filterNoBookingsText} />}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={sortedBookings.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        ariaLabel="Bokningslistans sidnavigering"
                    />
                </Fragment>
            )}

            {bookingId && <BookingModal bookingId={bookingId} hasMultipleObjects={false} onClose={() => setBookingId(undefined)} onRefresh={refetch} />}
        </Fragment>
    );
};

function getInvoiceStatusText(invoiceStatus: number, bookingStatus: BookingStatus | undefined, hasFreeCancellation: boolean): string {
    switch (invoiceStatus) {
        case InvoiceStatus.Cancelled:
            return 'Annulerad';
        case InvoiceStatus.HandledExternally:
            return 'Externt';
        case InvoiceStatus.Paid:
            return 'Betald';
        case InvoiceStatus.Pending:
            return 'Väntande';
        case InvoiceStatus.Created:
            return 'Skapad';
    }

    if (bookingStatus === BookingStatus.Cancelled && hasFreeCancellation) {
        return 'Fri avbokning';
    }

    return '';
}

function sortBookings(bookings: Booking[], sortOrder: BookingsSortOrder = BookingsSortOrder.None): Booking[] {
    if (!bookings) {
        return [];
    }

    const sortedBookings = bookings.slice();
    switch (sortOrder) {
        case BookingsSortOrder.Name:
            sortedBookings.sort(compareName);
            break;
        case BookingsSortOrder.Date:
            sortedBookings.sort(compareDates);
            break;
        case BookingsSortOrder.BookedBy:
            sortedBookings.sort(compareUserName);
            break;
        case BookingsSortOrder.Reference:
            sortedBookings.sort(compareReference);
            break;
        default:
            break;
    }

    return sortedBookings;
}

function compareReference(bookingA: Booking, bookingB: Booking): number {
    const bookingAReference = bookingA.bookingReference ?? '';
    const bookingBReference = bookingB.bookingReference ?? '';

    return bookingAReference.localeCompare(bookingBReference);
}

function compareName(bookingA: Booking, bookingB: Booking): number {
    const bookingAAssetname = bookingA?.assetName ?? '';
    const bookingBAssetname = bookingB?.assetName ?? '';

    return bookingBAssetname.toLocaleLowerCase().localeCompare(bookingAAssetname);
}

function compareUserName(bookingA: Booking, bookingB: Booking): number {
    const bookingAUsername = bookingA?.userName ?? '';
    const bookingBUsername = bookingB?.userName ?? '';

    return bookingBUsername.toLocaleLowerCase().localeCompare(bookingAUsername);
}

function compareDates(bookingA: Booking, bookingB: Booking): number {
    const bookingADate = new Date(bookingA?.startTime ?? 0).getTime();
    const bookingBDate = new Date(bookingB?.startTime ?? 0).getTime();

    return bookingBDate - bookingADate;
}

export default BookingList;
